import { IFilterElement } from "@saleor/components/Filter";
import {
  CouponFilterKeys,
  CouponListUrlFilterOpts
} from "@saleor/coupons/components/CouponListPage/filters";
import { InitialAllMallsFilterDataQuery_malls_edges_node } from "@saleor/coupons/types/InitialAllMallsFilterDataQuery";
import { InitialAssignedMallsFilterDataQuery_me_malls_edges_node } from "@saleor/coupons/types/InitialAssignedMallsFilterDataQuery";
import {
  SearchAssignedMalls,
  SearchAssignedMallsVariables
} from "@saleor/coupons/types/SearchAssignedMalls";
import {
  CouponsListUrlFilters,
  CouponsListUrlFiltersEnum,
  CouponsListUrlFilterWithMultipleValues,
  CouponsListUrlQueryParams
} from "@saleor/coupons/url";
import { UseSearchResult } from "@saleor/hooks/makeSearch";
import {
  findValueInEnum,
  joinDateTime,
  maybe,
  parseBoolean
} from "@saleor/misc";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";
import { CouponFilterInput, CouponStatusEnum } from "@saleor/types/globalTypes";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleEnumValueQueryParam,
  getMultipleValueQueryParam,
  getSingleValueQueryParam
} from "@saleor/utils/filters";
export const COUPON_FILTER_KEY = "couponFilters";

export function getFilterOpts(
  params: CouponsListUrlFilters,
  malls: {
    allMall: boolean;
    initialAssigned?: InitialAssignedMallsFilterDataQuery_me_malls_edges_node[];
    initialAllMalls?: InitialAllMallsFilterDataQuery_malls_edges_node[];
    searchAssigned?: UseSearchResult<
      SearchAssignedMalls,
      SearchAssignedMallsVariables
    >;
    searchAllMall?: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): CouponListUrlFilterOpts {
  let mallsFilterOpts;
  if (!malls.allMall) {
    mallsFilterOpts = {
      choices: maybe(
        () =>
          malls.searchAssigned.result.data.me.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.mall
        ? maybe(
            () =>
              malls.initialAssigned.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.searchAssigned.result.data.me.search.pageInfo.hasNextPage,
        false
      ),
      loading: malls.searchAssigned.result.loading,
      onFetchMore: malls.searchAssigned.loadMore,
      onSearchChange: malls.searchAssigned.search
    };
  } else {
    mallsFilterOpts = {
      choices: maybe(
        () =>
          malls.searchAllMall.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.mall
        ? maybe(
            () =>
              malls.initialAllMalls.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.searchAllMall.result.data.search.pageInfo.hasNextPage,
        false
      ),
      loading: malls.searchAllMall.result.loading,
      onFetchMore: malls.searchAllMall.loadMore,
      onSearchChange: malls.searchAllMall.search
    };
  }

  return {
    couponFeatured: {
      active: maybe(() => params.isFeatured !== undefined, false),
      value: maybe(() => parseBoolean(params.isFeatured, true))
    },
    effectivityDates: {
      active: maybe(
        () =>
          [params.startDate, params.endDate].some(field => field !== undefined),
        false
      ),
      value: {
        max: maybe(() => params.endDate, ""),
        min: maybe(() => params.startDate, "")
      }
    },
    mall: {
      active: !!params.mall,
      ...mallsFilterOpts,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    status: {
      active: maybe(() => params.status !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.status.map(status =>
              findValueInEnum(status, CouponStatusEnum)
            )
          ),
        []
      )
    }
  };
}

export function getFilterVariables(
  params: CouponsListUrlFilters
): CouponFilterInput {
  const startTime = "00:00";
  const endTime = "23:59";
  const mallArray = params.mall !== undefined ? Object.values(params.mall) : [];
  return {
    dealType: params.dealType ? dedupeFilter(params.dealType) : [],
    endDate: getGteLteVariables({
      gte: joinDateTime(params.startDate, startTime),
      lte: joinDateTime(params.endDate, endTime)
    }),
    isFeatured:
      params.isFeatured !== undefined
        ? parseBoolean(params.isFeatured, false)
        : undefined,
    participatingMalls: params.mall !== undefined ? mallArray : [],
    searchName: params.query,
    startDate: getGteLteVariables({
      gte: joinDateTime(params.startDate, startTime),
      lte: joinDateTime(params.endDate, endTime)
    }),
    status:
      params.status &&
      params.status.map(status => findValueInEnum(status, CouponStatusEnum))
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<CouponFilterKeys>
): CouponsListUrlFilters {
  const { name } = filter;
  switch (name) {
    case CouponFilterKeys.couponFeatured:
      return getSingleValueQueryParam(
        filter,
        CouponsListUrlFiltersEnum.couponFeatured
      );
    case CouponFilterKeys.status:
      return getMultipleEnumValueQueryParam(
        filter,
        CouponsListUrlFilterWithMultipleValues.status,
        CouponStatusEnum
      );
    case CouponFilterKeys.effectivityDates:
      return getMinMaxQueryParam(
        filter,
        CouponsListUrlFiltersEnum.startDate,
        CouponsListUrlFiltersEnum.endDate
      );
    case CouponFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        CouponsListUrlFilterWithMultipleValues.mall
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<CouponsListUrlFilters>(COUPON_FILTER_KEY);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  CouponsListUrlFilters,
  CouponsListUrlQueryParams
>({ ...CouponsListUrlFiltersEnum, ...CouponsListUrlFilterWithMultipleValues });
